import type { ComponentsDefinition } from '@eneco/cms';
import { cmsComponents } from '@eneco/cms-components';
import dynamic from 'next/dynamic';

/**
 * The CMS-agnostic registry for this container: the shared one plus what only this
 * container mounts.
 *
 * Applications are registered per container on the Sitecore lane too, in
 * `sitecoreComponents.ts`, and for the same reason: they cannot live in the shared
 * registry. `lib:cms-components` may not depend on `app:next` (see `depConstraints`
 * in `eslint.config.mjs`), and every container passes that one map, so a flow put
 * there would also be pulled into the containers that never render it.
 *
 * The cookie wall is one of those applications. It sits in the page's `jss-meta`
 * placeholder, which `CmsLayout` renders on this lane too, and reads its content
 * through `@eneco/cms`, so the same component serves both lanes. The chat sits there too; its CMS-lane entry
 * is `ChatInner`, which reads its content through `@eneco/cms`.
 *
 * The registration flow is registered under both its rendering names as its CMS-lane entry
 * (`RegistrationFlowInner`), which picks the variant from the rendering's name.
 *
 * The heat comparison tool is one too, registered as its inner component: this lane's
 * `Placeholder` already hands it its rendering, which the Sitecore lane's bridge builds itself.
 * The inspiration advice filter and the decarbonization potential breakdown are registered the same way.
 *
 * No flow is here yet. The B2B lead and decarbonization lead flows in
 * `sitecoreComponents.ts` still read Sitecore directly, so mounting them on this lane
 * would fail at the first step rather than render. Each is added as it migrates.
 */
const components: ComponentsDefinition = {
  ...cmsComponents,
  Chat: dynamic(() => import('@apps/chat-shared-multilabel/Chat').then(mod => mod.ChatInner)),
  CookieWall: dynamic(() => import('@apps/cookiewall-nl-multilabel/CookieWall')),
  DecarbonizationPotentialBreakdown: dynamic(() =>
    import('@apps/decarbonizationPotentialBreakdown-nl-eneco/DecarbonizationPotentialBreakdown').then(
      mod => mod.DecarbonizationPotentialBreakdownInner,
    ),
  ),
  HeatComparisonTool: dynamic(() =>
    import('@apps/calculator-nl-eneco/HeatComparisonCalculator').then(mod => mod.HeatComparisonCalculatorInner),
  ),
  InspirationAdviceFilter: dynamic(() =>
    import('@apps/inspirationadvicefilter-nl-eneco/InspirationAdviceFilter').then(
      mod => mod.InspirationAdviceFilterInner,
    ),
  ),
  RegistrationAccountCreate: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
  RegistrationAccountReset: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
};

export default components;
